import { type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Pricing } from "~/lib/api";
import { isMtbTrailRelatedPlace } from "~/lib/api";
import { PLACE_TAB_STATE_KEY } from "~/hooks/usePlaceNavigation";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "~/components/ui/tabs";
import { PlaceOverview, type PlaceOverviewProps } from "~/components/landing/PlaceOverview";
import { PlacePricing } from "~/components/place/PlacePricing";
import { PlaceTrails } from "~/components/place/PlaceTrails";

interface PlaceDetailTabsProps extends PlaceOverviewProps {
  pricing: Pricing | null | undefined;
  className?: string;
}

interface TabDef {
  value: string;
  label: string;
  content: ReactNode;
}

export function PlaceDetailTabs({ pricing, className, ...overviewProps }: PlaceDetailTabsProps) {
  const trails =
    overviewProps.type === "bike-park" ? (overviewProps.relatedPlaces?.filter(isMtbTrailRelatedPlace) ?? []) : [];

  const tabs: TabDef[] = [{ value: "overview", label: "Overview", content: <PlaceOverview {...overviewProps} /> }];
  if (trails.length > 0) {
    tabs.push({
      value: "trails",
      label: "Trails",
      content: <PlaceTrails trails={trails} onSelect={overviewProps.onSelectRelatedPlace} />,
    });
  }
  if (pricing && !pricing.free && pricing.prices.length > 0) {
    tabs.push({ value: "prices", label: "Prices", content: <PlacePricing pricing={pricing} /> });
  }

  const [activeTab, selectTab] = usePlaceTab(tabs.map((tab) => tab.value));

  if (tabs.length === 1) {
    return <div className={className}>{tabs[0].content}</div>;
  }

  return (
    <Tabs value={activeTab} onValueChange={selectTab} className={className}>
      <TabsList>
        {tabs.map((tab) => (
          <TabsTrigger key={tab.value} value={tab.value}>
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {tabs.map((tab) => (
        <TabsContent key={tab.value} value={tab.value}>
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  );
}

function usePlaceTab(available: string[]) {
  const location = useLocation();
  const navigate = useNavigate();

  const stored = (location.state as Record<string, unknown> | null)?.[PLACE_TAB_STATE_KEY];
  const tab = typeof stored === "string" && available.includes(stored) ? stored : "overview";

  const select = (next: string) => {
    navigate(location.pathname + location.search, {
      replace: true,
      state: { ...location.state, [PLACE_TAB_STATE_KEY]: next },
    });
  };

  return [tab, select] as const;
}
